import React from "react";
import {
  Award,
  Camera,
  Globe,
  MapPin,
  Wrench,
  Phone,
  Mail,
} from "lucide-react";
import type { BusinessFormData } from "../../../types/business";
import { Button, ButtonLink } from "../ui/button";

type QuickAction = {
  label: string;
  prompt: string;
};

const CHIP_CLASS =
  "px-3 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer bg-premium-50 text-premium-700 border border-premium-100 hover:bg-premium-100";

const PRODUCT_LINK_CLASS = `${CHIP_CLASS} flex items-center gap-1.5`;

const QUICK_ACTIONS: QuickAction[] = [
  {
    label: "Add a Service",
    prompt: "I'd like to add a new service",
  },
  {
    label: "Update Service Area",
    prompt: "Can you update my service area?",
  },
  {
    label: "Add Photos",
    prompt: "I want to add new project photos",
  },
];

type WelcomeMessageCardProps = {
  businessForm?: BusinessFormData;
  contactEmail?: string | null;
  isProcessing: boolean;
  billingRestricted: boolean;
  onQuickAction: (prompt: string) => void;
  onEnterLivePreview: () => void;
  jobsHref?: string;
  credentialsHref?: string;
};

export const WelcomeMessageCard: React.FC<WelcomeMessageCardProps> = ({
  businessForm,
  contactEmail,
  isProcessing,
  billingRestricted,
  onQuickAction,
  onEnterLivePreview,
  jobsHref,
  credentialsHref,
}) => {
  const services = businessForm?.services
    ? businessForm.services
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean)
    : [];

  const serviceAreas = businessForm?.serviceAreas
    ? businessForm.serviceAreas
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean)
    : [];

  const primaryArea = businessForm?.serviceArea;

  return (
    <div className="bg-card border border-border rounded-2xl rounded-tl-sm p-4 text-sm leading-relaxed shadow-sm">
      <p className="text-foreground font-medium mb-3">
        Hey! Here&apos;s your current business info:
      </p>

      <div className="rounded-xl border border-border bg-muted/40 p-4">
        {businessForm?.businessName && (
          <p className="text-base font-semibold leading-snug text-foreground">
            {businessForm.businessName}
          </p>
        )}

        <dl className="mt-3 space-y-3 text-sm leading-relaxed">
          {businessForm?.ownerName && (
            <div>
              <dt className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Owner
              </dt>
              <dd className="mt-0.5 break-words text-foreground">
                {businessForm.ownerName}
              </dd>
            </div>
          )}
          {businessForm?.tradeType && (
            <div>
              <dt className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Trade
              </dt>
              <dd className="mt-0.5 break-words text-foreground">
                {businessForm.tradeType}
              </dd>
            </div>
          )}
          {businessForm?.phone && (
            <div className="flex items-start gap-2 text-foreground">
              <Phone className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="min-w-0 break-words">{businessForm.phone}</span>
            </div>
          )}
          {(businessForm?.businessEmail || contactEmail) && (
            <div className="flex items-start gap-2 text-foreground">
              <Mail className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="min-w-0 break-words">
                {businessForm?.businessEmail || contactEmail}
              </span>
            </div>
          )}
          {(primaryArea || serviceAreas.length > 0) && (
            <div className="flex items-start gap-2">
              <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <dt className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Service area
                </dt>
                <dd className="mt-0.5 break-words text-foreground">
                  {[primaryArea, ...serviceAreas].filter(Boolean).join(", ")}
                </dd>
              </div>
            </div>
          )}
          {services.length > 0 && (
            <div className="flex items-start gap-2">
              <Wrench className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <dt className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Services
                </dt>
                <dd className="mt-0.5 break-words text-foreground">
                  {services.join(", ")}
                </dd>
              </div>
            </div>
          )}
        </dl>
      </div>

      {!billingRestricted && (
        <>
          <p className="mt-3 font-medium text-foreground">
            What would you like to update?
          </p>

          <div className="flex flex-wrap gap-2 mt-2.5">
            {QUICK_ACTIONS.map((action) => (
              <Button
                variant="surface"
                size="sm"
                disabled={isProcessing}
                key={action.label}
                onClick={() => onQuickAction(action.prompt)}
                className={CHIP_CLASS}
              >
                {action.label}
              </Button>
            ))}

            <Button
              variant="surface"
              size="sm"
              disabled={isProcessing}
              onClick={onEnterLivePreview}
              className={`${CHIP_CLASS} flex items-center gap-1.5`}
            >
              <Globe className="w-3 h-3" />
              Edit Website
            </Button>

            {jobsHref ? (
              <ButtonLink
                href={jobsHref}
                variant="surface"
                size="sm"
                className={PRODUCT_LINK_CLASS}
              >
                <Camera className="w-3 h-3" />
                Jobs
              </ButtonLink>
            ) : null}

            {credentialsHref ? (
              <ButtonLink
                href={credentialsHref}
                variant="surface"
                size="sm"
                className={PRODUCT_LINK_CLASS}
              >
                <Award className="w-3 h-3" />
                Credentials
              </ButtonLink>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
};
